import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import Footer from "@/components/site/Footer";
import PostBody from "@/components/site/PostBody";
import { renderInline } from "@/components/site/inlineFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return "";
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
    // Count a read once per browser session per post
    const key = `viewed_${slug}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      axios.post(`${API}/blog/${slug}/view`).catch(() => {});
    }
  }, [slug]);

  return (
    <div className="min-h-screen bg-twilight text-cream" data-testid="blog-post-page">
      <PageHeader />
      <main className="mx-auto max-w-3xl px-6 py-16 md:px-10 md:py-24">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-2 font-body text-sm text-slate-400 transition-colors duration-300 hover:text-gold"
        >
          <ArrowLeft className="h-4 w-4" /> All posts
        </Link>

        {post === undefined ? (
          <div className="flex justify-center py-24 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : post === null ? (
          <div className="py-24 text-center">
            <h1 className="font-display text-3xl text-cream">Post not found</h1>
            <Link to="/blog" className="mt-4 inline-block font-body text-gold">Back to the blog</Link>
          </div>
        ) : (
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8"
          >
            <time className="font-body text-sm uppercase tracking-[0.2em] text-gold">
              {fmtDate(post.published_at)}
            </time>
            <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-cream md:text-5xl">
              {renderInline(post.title)}
            </h1>
            <p className="mt-4 font-body text-sm text-slate-400">by Shaunta Scott</p>
            <div className="my-8 h-px w-full bg-white/10" />
            <PostBody body={post.body} />
          </motion.article>
        )}
      </main>
      <Footer minimal />
    </div>
  );
}
